// client/src/pages/org/OrgAdminsPage.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { ResponsiveTable, type ResponsiveColumn } from '@/components/ui/responsive-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgContext } from '@/hooks/useOrgContext';
import { getApiErrorStatus } from '@/lib/api-error';
import { createOrgAdmin, listOrgAdmins } from '@/services/api.client';
import type { OrgAdmin } from '@/types';
import { formatDateTime } from '@/lib/utils';

const schema = z.object({
  email: z.string().email('Valid email is required'),
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  phone: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

const ADMIN_COLUMNS: ResponsiveColumn<OrgAdmin>[] = [
  {
    key: 'name',
    header: 'Name',
    priority: 'primary',
    cell: (a) => <span className="font-medium">{a.fullName}</span>,
  },
  { key: 'email', header: 'Email', priority: 'primary', cell: (a) => a.email },
  {
    key: 'status',
    header: 'Status',
    priority: 'secondary',
    cell: (a) => <span className="capitalize">{a.status}</span>,
  },
  {
    key: 'lastLogin',
    header: 'Last Login',
    priority: 'secondary',
    cell: (a) => (a.lastLoginAt ? formatDateTime(a.lastLoginAt) : 'Never'),
  },
];

export function OrgAdminsPage() {
  const ctx = useOrgContext();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const organisationId = ctx?.organisationId ?? null;

  const adminsQuery = useQuery({
    queryKey: ['org-admins', organisationId],
    queryFn: () => listOrgAdmins(organisationId as string),
    enabled: !!organisationId,
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      createOrgAdmin(organisationId as string, {
        email: values.email,
        fullName: values.fullName,
        ...(values.phone !== undefined && { phone: values.phone }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-admins', organisationId] });
      toast.success('Org admin created');
      setOpen(false);
      reset();
    },
    onError: (err) => {
      const status = getApiErrorStatus(err);
      if (status === 409) {
        toast.error('A user with this email already exists');
      } else if (status === 422) {
        toast.error('Create at least one property before adding org admins');
      } else {
        toast.error('Failed to create org admin');
      }
    },
  });

  // Without an organisation context, the queries/mutations cannot fire — render
  // a non-crashing explanatory state instead of `ctx!`. Prior code threw here.
  if (!organisationId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl">Org Admins</h1>
        <p className="text-muted-foreground text-sm">Organisation context is unavailable.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Org Admins</h1>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4 mr-2" />
          Add Org Admin
        </Button>
      </div>

      <QueryStateBoundary
        query={adminsQuery}
        errorTitle="Couldn't load org admins"
        errorFallback="The admin list is unavailable. Try again in a moment."
        loadingFallback={<Skeleton className="h-40 w-full" />}
        emptyMessage="No org admins yet"
      >
        {(admins) => (
          <ResponsiveTable
            columns={ADMIN_COLUMNS}
            rows={admins}
            getRowKey={(a) => a.id}
            caption="Organisation administrators"
          />
        )}
      </QueryStateBoundary>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) reset();
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Org Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
            <FieldGroup className="py-2">
              <Field>
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="admin@example.com"
                />
                <FieldError errors={[errors.email]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
                <Input id="fullName" {...register('fullName')} placeholder="Jane Ochieng" />
                <FieldError errors={[errors.fullName]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="phone">Phone (optional)</FieldLabel>
                <Input id="phone" {...register('phone')} placeholder="+254700000001" />
              </Field>
            </FieldGroup>
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                Add
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
